(() => {
  const BaseQuickPersonalizer = customElements.get('tss-quick-personalizer');
  if (!BaseQuickPersonalizer || customElements.get('tss-quick-personalizer-3-line')) return;

  class TSSQuickPersonalizerThreeLine extends BaseQuickPersonalizer {
    connectedCallback() {
      this.lineInputs = [...this.querySelectorAll('[data-qp-line-input]')];
      this.previewLines = [...this.querySelectorAll('[data-qp-preview-line]')];
      super.connectedCallback();
      this.lineInputs = [...this.querySelectorAll('[data-qp-line-input]')];
      this.previewLines = [...this.querySelectorAll('[data-qp-preview-line]')];
      this.syncText();
    }

    bind() {
      super.bind();
      this.querySelectorAll('[data-qp-line-input]').forEach((input) => {
        if (input === this.textInput) return;
        input.addEventListener('input', () => { this.syncText(); this.validateForm(); this.saveDesign(); });
        input.addEventListener('focus', () => this.keepFieldVisible(input));
      });
    }

    getStorageKey() {
      return `tss-quick-personalizer-3line:${this.dataset.productHandle || location.pathname}`;
    }

    getLineInputs() {
      return [...this.querySelectorAll('[data-qp-line-input]')];
    }

    getLinePreview(name) {
      return this.querySelector(`[data-qp-preview-line="${name}"]`);
    }

    getLineInput(name) {
      return this.querySelector(`[data-qp-line-input="${name}"]`);
    }

    requiredLinesComplete() {
      return this.getLineInputs().filter((input) => input.required).every((input) => Boolean(input.value.trim()));
    }

    anyPersonalizedText() {
      return this.getLineInputs().some((input) => Boolean(input.value.trim()));
    }

    findStepIndex(selector) {
      const found = this.mobileSteps?.findIndex((step) => Boolean(step.querySelector(selector)));
      return found >= 0 ? found : 0;
    }

    stepIsComplete(index) {
      const step = this.mobileSteps?.[index];
      if (!step) return true;
      if (step.querySelector('[data-qp-variant]')) return Boolean(this.querySelector('[data-qp-variant]')?.value);
      if (step.querySelector('[data-qp-line-input]')) return this.requiredLinesComplete();
      if (step.querySelector('[data-qp-holes]')) return !this.requireHoles || Boolean(this.querySelector('[data-qp-holes]')?.value);
      if (step.querySelector('[data-qp-quantity]')) return Number(this.quantity?.value || 0) >= 1;
      return true;
    }

    getMissingRequirements() {
      const missing = [];
      if (!this.querySelector('[data-qp-variant]')?.value) missing.push({ label:'Choose a size', target:this.findStepIndex('[data-qp-variant]') });
      this.getLineInputs().forEach((input) => {
        if (input.required && !input.value.trim()) {
          missing.push({ label:`Enter ${input.dataset.qpLineLabel || 'required text'}`, target:this.findStepIndex('[data-qp-line-input]') });
        }
      });
      if (this.requireHoles && !this.querySelector('[data-qp-holes]')?.value) missing.push({ label:'Choose hole placement', target:this.findStepIndex('[data-qp-holes]') });
      if (Number(this.quantity?.value || 0) < 1) missing.push({ label:'Choose a quantity', target:this.findStepIndex('[data-qp-quantity]') });
      return missing;
    }

    validateForm(showErrors = false) {
      const inputs = this.getLineInputs();
      const firstMissing = inputs.find((input) => input.required && !input.value.trim());
      const textValid = !firstMissing;
      const holes = this.querySelector('[data-qp-holes]');
      const holesValid = !this.requireHoles || Boolean(holes?.value);
      const quantityValid = Number(this.quantity?.value || 0) >= 1;
      const approvalValid = !this.requireApproval || Boolean(this.approval?.checked);
      const sizeValid = Boolean(this.querySelector('[data-qp-variant]')?.value);
      const valid = textValid && holesValid && quantityValid && approvalValid && sizeValid && this.variantAvailable;
      this.updateRequiredNotice();
      this.submit?.setAttribute('aria-disabled', valid ? 'false' : 'true');

      const textField = this.querySelector('[data-qp-line-input]')?.closest('.tss-qp__field');
      if (valid) {
        textField?.classList.remove('is-invalid');
        const status = this.querySelector('.tss-qp__status');
        if (status) status.textContent = '';
      }
      if (showErrors && !valid) {
        textField?.classList.toggle('is-invalid', !textValid);
        if (firstMissing) {
          const stepIndex = this.findStepIndex('[data-qp-line-input]');
          if (this.mobileSteps?.[stepIndex]) this.mobileSteps[stepIndex].open = true;
          firstMissing.focus({ preventScroll:true });
          firstMissing.scrollIntoView({ behavior:'smooth', block:'center' });
        } else if (!holesValid) {
          const stepIndex = this.findStepIndex('[data-qp-holes]');
          if (this.mobileSteps?.[stepIndex]) this.mobileSteps[stepIndex].open = true;
          holes?.focus({ preventScroll:true });
          holes?.scrollIntoView({ behavior:'smooth', block:'center' });
        } else if (!approvalValid && this.approvalWrap) {
          this.approvalWrap.classList.remove('is-alerting');
          void this.approvalWrap.offsetWidth;
          this.approvalWrap.classList.add('is-alerting');
          this.approval?.focus({ preventScroll:true });
          this.approvalWrap.scrollIntoView({ behavior:'smooth', block:'center' });
        }
        const status = this.querySelector('.tss-qp__status');
        if (status) {
          status.textContent = firstMissing ? `Please enter ${firstMissing.dataset.qpLineLabel || 'the required wording'}.` : !holesValid ? 'Please choose a hole placement option.' : !approvalValid ? 'Please approve your selection before adding the sign to your cart.' : 'Please complete all required selections.';
        }
      }
      return valid;
    }

    syncAll() {
      this.syncText();
      this.syncHoles();
      this.syncDoubleSided();
      this.syncPrice();
      this.validateForm();
      this.updateReview();
    }

    syncText() {
      const inputs = this.getLineInputs();
      this.previewWrap?.classList.toggle('has-personalized-text', inputs.some((input) => Boolean(input.value.trim())));
      inputs.forEach((input) => {
        const name = input.dataset.qpLineInput;
        const preview = this.getLinePreview(name);
        const display = input.value || input.placeholder || '';
        if (preview) preview.textContent = preview.dataset.qpUppercase === 'true' ? display.toUpperCase() : display;
        const count = this.querySelector(`[data-qp-line-count="${name}"]`);
        if (count) count.textContent = `${input.value.length}/${input.maxLength}`;
      });
      this.fitText();
      if (this.requiredLinesComplete()) this.scheduleProofPreview();
      this.updateReview();
      this.updateStepButtons();
    }

    saveDesign() {
      clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => {
        try {
          const lines = {};
          this.getLineInputs().forEach((input) => { lines[input.dataset.qpLineInput] = input.value || ''; });
          localStorage.setItem(this.getStorageKey(), JSON.stringify({
            variant:this.querySelector('[data-qp-variant]')?.value,
            lines,
            holes:this.querySelector('[data-qp-holes]')?.value,
            quantity:this.quantity?.value || '1',
            notes:this.querySelector('[name="properties[Notes/Format Requests]"]')?.value || '',
            doubleSided:Boolean(this.doubleSided?.checked)
          }));
        } catch (_error) {}
      }, 250);
    }

    restoreDesign() {
      try {
        const saved = JSON.parse(localStorage.getItem(this.getStorageKey()) || 'null');
        if (!saved) return;
        const variant = this.querySelector('[data-qp-variant]');
        const holes = this.querySelector('[data-qp-holes]');
        const notes = this.querySelector('[name="properties[Notes/Format Requests]"]');
        if (variant && [...variant.options].some((item) => item.value === saved.variant)) variant.value = saved.variant;
        this.getLineInputs().forEach((input) => { input.value = saved.lines?.[input.dataset.qpLineInput] || ''; });
        if (holes && [...holes.options].some((item) => item.value === saved.holes)) holes.value = saved.holes;
        if (this.quantity) this.quantity.value = Math.max(1, Number(saved.quantity || 1));
        if (notes) notes.value = saved.notes || '';
        if (this.doubleSided) this.doubleSided.checked = Boolean(saved.doubleSided);
      } catch (_error) {}
    }

    updateReview() {
      const value = (selector) => this.querySelector(selector);
      const variant = value('[data-qp-variant]')?.selectedOptions?.[0]?.textContent?.split('—')[0]?.trim() || '—';
      const quantity = Math.max(1, Number(this.quantity?.value || 1));
      const total = value('[data-qp-total-price]')?.value || '';
      const lineValue = (name) => this.getLineInput(name)?.value.trim() || 'Add wording above';
      const pairs = {
        '[data-qp-review-size]':variant,
        '[data-qp-review-top]':lineValue('top'),
        '[data-qp-review-main]':lineValue('main'),
        '[data-qp-review-bottom]':lineValue('bottom'),
        '[data-qp-review-holes]':value('[data-qp-holes]')?.value || '—',
        '[data-qp-review-printing]':this.doubleSided?.checked?'Double-sided':'Single-sided',
        '[data-qp-review-quantity]':String(quantity),
        '[data-qp-review-total]':total
      };
      Object.entries(pairs).forEach(([selector, content]) => { const target=value(selector); if (target) target.textContent=content; });
      const mobileTotal = value('[data-qp-mobile-total]');
      if (mobileTotal) mobileTotal.textContent = total;
    }

    scheduleProofPreview() {
      this.assetsReady = false;
      clearTimeout(this.proofTimer);
      this.proofTimer = setTimeout(async () => {
        if (!this.requiredLinesComplete() || !this.currentDimensions) return;
        try {
          const proof = await this.renderArtwork('proof');
          if (this.proofPreview) this.proofPreview.src = proof.dataUrl;
          if (this.proofCard) this.proofCard.hidden = false;
        } catch (_error) {}
      }, 350);
    }

    async renderArtwork(kind) {
      const dimensions = this.currentDimensions;
      if (!dimensions) throw new Error('Please choose a standard sign size.');
      const maxEdge = 15000;
      const maxPixels = 40000000;
      let dpi;
      if (kind === 'print') {
        dpi = Math.max(150, Math.min(300, Math.floor(maxEdge / Math.max(dimensions.width, dimensions.height)), Math.floor(Math.sqrt(maxPixels / (dimensions.width * dimensions.height)))));
      } else {
        dpi = Math.min(1200 / dimensions.width, 500 / dimensions.height);
      }
      const width = Math.max(1, Math.round(dimensions.width * dpi));
      const height = Math.max(1, Math.round(dimensions.height * dpi));
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      const context = canvas.getContext('2d', { alpha:false });
      const backgroundColor = this.dataset.qp3Background || '#000000';
      const stripeColor = this.dataset.qp3Stripe || '#002d7e';
      const stripeTop = this.numberDataset('qp3StripeTop', 35.4) / 100;
      const stripeHeight = this.numberDataset('qp3StripeHeight', 29.3) / 100;
      context.fillStyle = backgroundColor;
      context.fillRect(0,0,width,height);
      context.fillStyle = stripeColor;
      context.fillRect(0,height*stripeTop,width,height*stripeHeight);
      this.drawCanvasText(context,width,height);
      const type = kind === 'print' ? 'image/png' : 'image/jpeg';
      const blob = await new Promise((resolve) => canvas.toBlob(resolve,type,kind === 'print' ? undefined : .9));
      if (!blob) throw new Error('The personalized artwork could not be generated.');
      return { blob, width, height, dpi:kind === 'print' ? dpi : null, dataUrl:kind === 'proof' ? canvas.toDataURL('image/jpeg',.86) : null };
    }

    numberDataset(key, fallback) {
      const value = Number(this.dataset[key]);
      return Number.isFinite(value) ? value : fallback;
    }

    lineDefinition(name) {
      const preview = this.getLinePreview(name);
      const input = this.getLineInput(name);
      if (!preview || !input) return null;
      return {
        name, preview, input,
        y:Number(preview.dataset.qpLineY || 50),
        width:Number(preview.dataset.qpLineWidth || 80),
        height:Number(preview.dataset.qpLineHeight || 14),
        spacing:Number(preview.dataset.qpLineSpacing || 0),
        uppercase:preview.dataset.qpUppercase === 'true'
      };
    }

    drawCanvasText(context,width,height) {
      ['top','main','bottom'].forEach((name) => {
        const line = this.lineDefinition(name);
        if (!line) return;
        let text = line.input.value.trim();
        if (!text) return;
        if (line.uppercase) text = text.toUpperCase();
        const styles = getComputedStyle(line.preview);
        const family = styles.fontFamily || 'Arial, sans-serif';
        const weight = styles.fontWeight || '400';
        const color = styles.color || '#ffffff';
        const maxWidth = width * line.width / 100;
        const maxHeight = height * line.height / 100;
        const measureAt = 100;
        context.font = `${weight} ${measureAt}px ${family}`;
        const measured = context.measureText(text || ' ').width + Math.max(0,text.length-1) * line.spacing * measureAt;
        const widthFit = maxWidth * measureAt / Math.max(1,measured);
        const fontSize = Math.max(8, Math.min(widthFit, maxHeight * .88));
        context.font = `${weight} ${fontSize}px ${family}`;
        context.fillStyle = color;
        context.textBaseline = 'middle';
        context.textAlign = 'left';
        this.drawSpacedLine(context,text,width/2,height*line.y/100,fontSize*line.spacing);
      });
    }

    fitText() {
      if (!this.preview) return;
      const canvas = this._measureCanvas || (this._measureCanvas=document.createElement('canvas'));
      const context = canvas.getContext('2d');
      ['top','main','bottom'].forEach((name) => {
        const line = this.lineDefinition(name);
        if (!line) return;
        const text = line.preview.textContent || ' ';
        const styles = getComputedStyle(line.preview);
        const family = styles.fontFamily || 'Arial, sans-serif';
        const weight = styles.fontWeight || '400';
        context.font = `${weight} 100px ${family}`;
        const measured = context.measureText(text).width + Math.max(0,text.length-1) * line.spacing * 100;
        const maxWidth = Math.max(1,this.preview.clientWidth * line.width / 100);
        const maxHeight = Math.max(1,this.preview.clientHeight * line.height / 100);
        const widthFit = maxWidth * 100 / Math.max(1,measured);
        const fontSize = Math.max(8,Math.min(220,widthFit,maxHeight*.88));
        line.preview.style.fontSize = `${fontSize}px`;
      });
    }
  }

  customElements.define('tss-quick-personalizer-3-line', TSSQuickPersonalizerThreeLine);
})();
